// OWNER: LG-ADMIN. Every /api/leadgen call the Lead Search page makes, as {method, path}, in one pure place (no DOM, no
// fetch). endpoints.ts sends exactly these; tests/lg-admin-paths.test.ts checks each against the route table in
// src/shared/api-leadgen.ts and against the routes LG-DATA registers in src/worker/routes/leadgen.ts.
import type { Country } from '../../../src/shared/api';
import type { LeadResultsQuery } from '../../../src/shared/api-leadgen';

export const LEADGEN_BASE = '/api/leadgen';
export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH';

/** ?a=1&b=x, skipping undefined / null / '' (false is kept: hasHook=false is a real filter). */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

const seg = (s: string | number): string => encodeURIComponent(String(s));
const B = LEADGEN_BASE;

/** Keys are the leadgenApi function names in endpoints.ts. */
export const LEADGEN_CALLS = {
  niches: { method: 'GET', path: () => `${B}/niches` },
  proposeNiche: { method: 'POST', path: () => `${B}/niches/propose` },
  updateNiche: { method: 'PUT', path: (key: string, v: number) => `${B}/niches/${seg(key)}/${seg(v)}` },
  approveNiche: { method: 'POST', path: (key: string, v: number) => `${B}/niches/${seg(key)}/${seg(v)}/approve` },
  activateNiche: { method: 'POST', path: (key: string, v: number) => `${B}/niches/${seg(key)}/${seg(v)}/activate` },
  geo: { method: 'GET', path: (country: Country) => `${B}/geo${qs({ country })}` },

  createSearch: { method: 'POST', path: () => `${B}/searches` },
  estimate: { method: 'POST', path: (id: number) => `${B}/searches/${seg(id)}/estimate` },
  run: { method: 'POST', path: (id: number) => `${B}/searches/${seg(id)}/run` },
  pause: { method: 'POST', path: (id: number) => `${B}/searches/${seg(id)}/pause` },
  resume: { method: 'POST', path: (id: number) => `${B}/searches/${seg(id)}/resume` },
  cancel: { method: 'POST', path: (id: number) => `${B}/searches/${seg(id)}/cancel` },
  patchSearch: { method: 'PATCH', path: (id: number) => `${B}/searches/${seg(id)}` },
  searches: { method: 'GET', path: (limit?: number, offset?: number) => `${B}/searches${qs({ limit, offset })}` },
  search: { method: 'GET', path: (id: number) => `${B}/searches/${seg(id)}` },
  results: {
    method: 'GET',
    path: (id: number, q: LeadResultsQuery) => `${B}/searches/${seg(id)}/results${qs({
      tier: q.tier, status: q.status, state: q.state, flag: q.flag, drop: q.drop, hasHook: q.hasHook, page: q.page, pageSize: q.pageSize,
    })}`,
  },
  // P0-8: the export carries the table's filters (the server applies the same resultsFilter to the CSV).
  resultsCsvUrl: {
    method: 'GET',
    path: (id: number, q: LeadResultsQuery = {}) => `${B}/searches/${seg(id)}/results.csv${qs({
      tier: q.tier, status: q.status, state: q.state, flag: q.flag, drop: q.drop, hasHook: q.hasHook,
    })}`,
  },
  review: { method: 'POST', path: (id: number) => `${B}/searches/${seg(id)}/review` },
  importToCampaign: { method: 'POST', path: (id: number) => `${B}/searches/${seg(id)}/import` },
  placeDrawer: { method: 'GET', path: (leadPlaceId: number) => `${B}/places/${seg(leadPlaceId)}/drawer` },

  nextCheck: { method: 'GET', path: (niche: string, detector: string) => `${B}/checks/next${qs({ niche, detector })}` },
  checkBatch: { method: 'GET', path: (niche: string, detector: string, n: number) => `${B}/checks/batch${qs({ niche, detector, n })}` },
  submitCheck: { method: 'POST', path: () => `${B}/checks` },
  evals: { method: 'GET', path: (niche: string) => `${B}/evals${qs({ niche })}` },

  spend: { method: 'GET', path: (month?: string) => `${B}/spend${qs({ month })}` },
  suppression: { method: 'GET', path: () => `${B}/suppression` },
  updateSuppression: { method: 'PUT', path: () => `${B}/suppression` },
  calibration: { method: 'GET', path: (niche: string) => `${B}/calibration${qs({ niche })}` },

  research: { method: 'POST', path: (businessId: number) => `${B}/research/${seg(businessId)}` },
  apolloReveal: { method: 'POST', path: () => `${B}/apollo/reveal` },
} as const satisfies Record<string, { method: HttpMethod; path: (...args: never[]) => string }>;

export type LeadgenCall = keyof typeof LEADGEN_CALLS;
